import {
  forwardRef,
  useImperativeHandle,
  useState,
  type ReactNode,
} from 'react';

import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';

export type AnimatedRootHandler = {
  startCloseAnimation(): void;
};

type RootProps = {
  children: ReactNode;
  className: string;
  onCloseComplete?(): void;
  withoutPadding?: boolean;
};

export const AnimatedDialog = forwardRef<AnimatedRootHandler, RootProps>(
  ({ children, className, onCloseComplete, withoutPadding = false }, ref) => {
    const [open, setOpen] = useState(true);

    useImperativeHandle(ref, () => ({
      startCloseAnimation() {
        setOpen(false);
      },
    }));

    const handleAnimationEnd = () => {
      if (!open) {
        onCloseComplete?.();
      }
    };

    return (
      <dialog
        aria-modal="true"
        className={twMerge(
          `flex max-h-[calc(100%-24px)] w-full min-w-[360px] flex-col gap-m
          rounded-t-container bg-foundation-inverted
          desktop:max-h-[calc(100%-48px)] desktop:w-fit desktop:max-w-[800px]
          desktop:rounded-container`,
          className,
          open
            ? 'animate-slide-in-up desktop:animate-fade-in-up'
            : 'animate-slide-out-down desktop:animate-fade-out-down',
          withoutPadding ? 'p-0' : 'p-m',
        )}
        data-testid="animated-dialog"
        onAnimationEnd={handleAnimationEnd}
        open
      >
        {children}
      </dialog>
    );
  },
);

AnimatedDialog.displayName = 'AnimatedDialog';
